/**
 * One framed block of the concept panel.
 *
 * The panel used to be flat sections separated by rules, with each ROW
 * carrying a `bg-muted` chip. That inverts the weight: the container is
 * invisible and every individual item is a heavy grey slab, so a list of five
 * related concepts reads as five buttons shouting rather than as one list.
 *
 * `PrerequisitesBox` already had the right shape — a light bordered card with
 * plain rows inside it — and this is that shape, extracted so the other
 * sections can share it. The frame carries the grouping, and the rows stay
 * quiet until you point at one.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export function PanelSection({
  icon,
  title,
  hint,
  children,
  className,
}: {
  icon: ReactNode;
  title: string;
  /** One line under the heading, for sections whose meaning is not obvious. */
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      aria-label={title}
      className={cn(
        'rounded-lg border border-border bg-muted/40 px-3 py-2.5',
        className
      )}
    >
      <h4 className='flex items-center gap-1.5 font-mono text-xs uppercase tracking-wide text-muted-foreground'>
        {icon}
        {title}
      </h4>
      {hint && (
        <p className='mt-1 text-xs leading-relaxed text-muted-foreground'>
          {hint}
        </p>
      )}
      <div className='mt-1.5'>{children}</div>
    </section>
  );
}

/**
 * A row inside a `PanelSection`: no fill at rest, a tint on hover.
 *
 * Exported as a class rather than a component because the rows differ — some
 * are buttons, some are plain text, some carry a trailing figure — and the
 * thing worth sharing is only the surface treatment.
 */
export const PANEL_ROW =
  'group flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-sm text-foreground transition-colors max-sm:min-h-11';

export const PANEL_ROW_INTERACTIVE = `${PANEL_ROW} hover:bg-muted`;
